import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { KYDEvent } from "@common/interfaces/KYDEvent";
import { useForm } from "react-hook-form";
import * as y from "yup";

const CreateEventSchema = y.object({
  name: y.string().required(),
  subtitle: y.string().optional(),
  display_mode: y.mixed<KYDEvent["display_mode"]>().oneOf(["rsvp", "normal"]),
  age_restrictions_select: y.string().optional(),
  age_restrictions_input: y.string().optional(),
});

export type CreateEventValues = y.InferType<typeof CreateEventSchema>;

const CreateEventModal = ({ onClose, isOpen, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<CreateEventValues>();
  const onSubmit = (data: CreateEventValues) => {
    onSave(data);
  };

  return (
    <Modal
      size={"xl"}
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Event</ModalHeader>
        <ModalBody>
          <Stack spacing={6}>
            <Stack spacing={1}>
              <Text fontWeight={"600"}>Event Name</Text>
              <Input
                placeholder="Event Name"
                isDisabled={isLoading}
                {...register("name")}
              />
            </Stack>
            <Stack spacing={1}>
              <Stack spacing={0}>
                <HStack>
                  <Text fontWeight={"medium"}>Event Subtitle</Text>
                  <Text fontWeight={"400"}>(Optional)</Text>
                </HStack>
                <Text fontSize={"sm"} color={"gray.500"}>
                  Shown below the event name
                </Text>
              </Stack>
              <Input
                placeholder="Event Subtitle"
                isDisabled={isLoading}
                {...register("subtitle")}
              />
            </Stack>
            <Stack
              direction={["column", null, "row"]}
              spacing={8}
              align="flex-start"
            >
              <Stack spacing={1} flex="1.2" w="100%">
                <Text fontWeight={"medium"}>Event Type</Text>
                <Select
                  defaultValue={"normal"}
                  w={["100%", null, "150px"]}
                  {...register("display_mode")}
                >
                  <option value="normal">Paid Event</option>
                  <option value="rsvp">RSVP Event</option>
                </Select>
              </Stack>
              <Stack spacing={1} flex="3" w="100%">
                <Text fontWeight={"medium"}>Age Limit (Optional)</Text>
                <Stack direction={["column", null, "row"]} spacing={2}>
                  <Select
                    name="age_restrictions_select"
                    {...register("age_restrictions_select")}
                    isDisabled={isLoading}
                    w={["100%", null, "120px"]}
                  >
                    <option value="">None</option>
                    <option value="18+">18+</option>
                    <option value="21+">21+</option>
                    <option value="custom">Custom...</option>
                  </Select>
                  {watch("age_restrictions_select") === "custom" && (
                    <Input
                      name="age_restrictions_input"
                      {...register("age_restrictions_input")}
                      flex="1.8"
                      minH="40px"
                      placeholder="e.g. 16+ w/ guardian"
                      isDisabled={isLoading}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={isLoading}
            onClick={() => {
              onClose();
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            isDisabled={isLoading || !isValid}
            bg="blue.500"
            color="white"
            type="submit"
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateEventModal;
